import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import DesktopAppContext from './DesktopApp.context';
import { getPlatform, sentryError } from 'utils';
import dataRoomApi from '../../dataRoomApi';
import env from 'env';
import {
  APP_OS,
  DesktopAppDownloadedEventProps,
  DownloadSource,
} from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import useMatchMedia from 'hooks/useMatchMedia';

export const SHOW_MAXIMUM_SECURITY = env.SHOW_DR_MAXIMUM_SECURITY === 'true';
const DESKTOP_APP_DOWNLOADS_QUERY_KEY = 'desktop-app-downloads';

const DesktopApp: React.FC = (props) => {
  const { children } = props;

  const platform = useMemo(getPlatform, []);

  const getMixpanelProps = useGetMixpanelProps();

  const isTouchDevice = useMatchMedia('isTouchDevice');

  const canDownloadApp = !isTouchDevice && SHOW_MAXIMUM_SECURITY;

  const { data } = useQuery({
    queryKey: [DESKTOP_APP_DOWNLOADS_QUERY_KEY],
    queryFn: async () => {
      const { data } = await dataRoomApi.desktopApp.getDownloads();
      return data;
    },
    enabled: canDownloadApp,
    onError: sentryError,
  });

  const downloadLink = useMemo(() => {
    if (!canDownloadApp) return '';

    const [latestVersion] = data?.versions ?? [];

    const download = latestVersion?.downloads.find(
      (download) => download.platform === platform,
    );

    return download?.url ?? '';
  }, [canDownloadApp, data?.versions, platform]);

  const trackDownload = useCallback(
    (
      downloadSource: DownloadSource,
      dataRoomId: string,
      dataRoomName: string,
    ) => {
      mixpanelService.desktopAppDownloaded(
        getMixpanelProps({
          downloadSource,
          appOs: APP_OS[platform],
          dataRoomId,
          dataRoomName,
        }) as DesktopAppDownloadedEventProps,
      );
    },
    [getMixpanelProps, platform],
  );

  return (
    <DesktopAppContext.Provider value={{ downloadLink, trackDownload }}>
      {children}
    </DesktopAppContext.Provider>
  );
};

export default DesktopApp;
